import React from 'react';
import {
  Box,
  Typography,
  IconButton,
  Chip,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  useTheme,
  Tooltip
} from '@mui/material';
import {
  Notifications,
  Warning,
  Info,
  Error as ErrorIcon,
  CheckCircle,
  Close,
  AccessTime,
  Cake,
  WorkOutline,
  EventNote,
  Description,
  Badge as BadgeIcon,
  PersonAdd,
  Article,
  EmojiEvents,
  Notes as NotesIcon,
  Assignment
} from '@mui/icons-material';
import { Notification } from '@/store/NotificationStore';

interface NotificationItemProps {
  notification: Notification;
  onMarkAsRead?: (id: number) => void;
  onDismiss?: (id: number) => void;
  onClick?: (notification: Notification) => void;
  compact?: boolean;
  showDivider?: boolean;
}

// Clean mention markup from notification text for display
// Converts @[Name](type:id) to @Name
const cleanMentionFormat = (text: string | undefined): string => {
  if (!text) return '';
  return text.replace(/@\[([^\]]+)\]\((user|team):[^)]+\)/g, '@$1');
};

const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onMarkAsRead,
  onDismiss,
  onClick,
  compact = false,
  showDivider = true
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const getPriorityColor = (priority: Notification['priority']) => {
    switch (priority) {
      case 'urgent':
        return '#F44336';
      case 'high':
        return '#FF9800';
      case 'normal':
        return '#2196F3';
      case 'low':
        return '#4CAF50';
      default:
        return theme.palette.text.secondary;
    }
  };

  const getAlertTypeIcon = (alertType: string) => {
    switch (alertType) {
      case 'birthday':
        return <Cake sx={{ color: '#E91E63', fontSize: compact ? 18 : 20 }} />;
      case 'work_anniversary':
        return <WorkOutline sx={{ color: '#9C27B0', fontSize: compact ? 18 : 20 }} />;
      case 'probation_ending':
        return <AccessTime sx={{ color: '#FF5722', fontSize: compact ? 18 : 20 }} />;
      case 'leave_balance_low':
        return <EventNote sx={{ color: '#FF9800', fontSize: compact ? 18 : 20 }} />;
      case 'pending_approval':
        return <AccessTime sx={{ color: '#2196F3', fontSize: compact ? 18 : 20 }} />;
      case 'document_expiring':
        return <Description sx={{ color: '#795548', fontSize: compact ? 18 : 20 }} />;
      case 'certification_expiring':
        return <BadgeIcon sx={{ color: '#607D8B', fontSize: compact ? 18 : 20 }} />;
      case 'new_user':
        return <PersonAdd sx={{ color: '#4CAF50', fontSize: compact ? 18 : 20 }} />;
      case 'new_feed':
        return <Article sx={{ color: '#2196F3', fontSize: compact ? 18 : 20 }} />;
      case 'wall_of_fame':
        return <EmojiEvents sx={{ color: '#FFC107', fontSize: compact ? 18 : 20 }} />;
      case 'okr_note_mention':
        return <NotesIcon sx={{ color: '#f59e0b', fontSize: compact ? 18 : 20 }} />;
      case 'task_assigned':
        return <Assignment sx={{ color: '#6366f1', fontSize: compact ? 18 : 20 }} />;
      case 'custom':
        return <Notifications sx={{ color: theme.palette.primary.main, fontSize: compact ? 18 : 20 }} />;
      default:
        return <Notifications sx={{ color: theme.palette.text.secondary, fontSize: compact ? 18 : 20 }} />;
    }
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  const handleClick = () => {
    if (!notification.is_read && onMarkAsRead) {
      onMarkAsRead(notification.id);
    }
    if (onClick) {
      onClick(notification);
    }
  };

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDismiss) {
      onDismiss(notification.id);
    }
  };

  return (
    <ListItem
      sx={{
        py: compact ? 1 : 1.5,
        px: 2,
        borderBottom: showDivider
          ? `1px solid ${isDark ? 'rgba(255, 255, 255, 0.05)' : theme.palette.divider}`
          : 'none',
        backgroundColor: !notification.is_read
          ? isDark ? 'rgba(33, 150, 243, 0.1)' : 'rgba(33, 150, 243, 0.05)'
          : 'transparent',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'background-color 0.2s ease',
        '&:hover': {
          backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : theme.palette.action.hover,
          '& .dismiss-button': {
            opacity: 1
          }
        }
      }}
      onClick={handleClick}
    >
      <ListItemIcon sx={{ minWidth: compact ? 40 : 44 }}>
        <Box
          sx={{
            width: compact ? 32 : 36,
            height: compact ? 32 : 36,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: `${getPriorityColor(notification.priority)}15`,
            border: `1px solid ${getPriorityColor(notification.priority)}30`
          }}
        >
          {getAlertTypeIcon(notification.alert_type)}
        </Box>
      </ListItemIcon>
      <ListItemText
        primary={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: notification.is_read ? 400 : 600,
                color: theme.palette.text.primary,
                flex: 1,
                fontSize: compact ? '0.8125rem' : '0.875rem'
              }}
              noWrap
            >
              {notification.title}
            </Typography>
            {notification.priority === 'urgent' && (
              <Chip
                label="Urgent"
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.65rem',
                  fontWeight: 600,
                  backgroundColor: '#F4433620',
                  color: '#F44336',
                  border: '1px solid #F4433640'
                }}
              />
            )}
            {notification.priority === 'high' && (
              <Chip
                label="High"
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.65rem',
                  fontWeight: 600,
                  backgroundColor: '#FF980020',
                  color: '#FF9800',
                  border: '1px solid #FF980040'
                }}
              />
            )}
          </Box>
        }
        secondary={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
              sx={{ flex: 1, fontSize: compact ? '0.7rem' : '0.75rem' }}
            >
              {cleanMentionFormat(notification.description) || formatTimeAgo(notification.created_at)}
            </Typography>
            {!compact && notification.due_date && (
              <Chip
                size="small"
                icon={<AccessTime sx={{ fontSize: '0.75rem !important' }} />}
                label={new Date(notification.due_date).toLocaleDateString()}
                sx={{
                  height: 18,
                  fontSize: '0.65rem',
                  '& .MuiChip-icon': { ml: 0.5 }
                }}
              />
            )}
            {compact && (
              <Typography
                variant="caption"
                color="text.disabled"
                sx={{ fontSize: '0.65rem', flexShrink: 0 }}
              >
                {formatTimeAgo(notification.created_at)}
              </Typography>
            )}
          </Box>
        }
      />
      {onDismiss && (
        <ListItemSecondaryAction>
          <Tooltip title="Dismiss">
            <IconButton
              size="small"
              className="dismiss-button"
              onClick={handleDismiss}
              sx={{
                opacity: 0,
                transition: 'opacity 0.2s ease',
                '&:hover': {
                  backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.04)'
                }
              }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Tooltip>
        </ListItemSecondaryAction>
      )}
    </ListItem>
  );
};

export default NotificationItem;
